import React from "react";
import { Space } from "antd";
import { AxButton, AxDivider } from "@dynata/axiom3";

class Button extends React.Component {
  render() {
    return (
      <div className="section button" id="button">
        <h3>Buttons</h3>
        <Space direction="vertical">
          <p>
            The button elements are available in 4 different color styles. Each
            category has default, active and disabled button states. Button
            categories are distinguishable through their separate colors and
            types as shown below.
          </p>
        </Space>

        <AxDivider orientation="left" plain>
          Basic Usage
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`<AxButton color="main">Button</AxButton>`}</pre>

          <div className="prop-header">
            <p className="props-title">Props</p>
            <p>
              For AntD props, visit{" "}
              <AxButton
                size="small"
                type="link"
                target="_blank"
                href="https://ant.design/components/button/#API"
              >
                https://ant.design/components/button/#API
              </AxButton>
            </p>
          </div>
          <table className="props-table">
            <thead>
              <tr>
                <th>Property</th>
                <th>Description</th>
                <th>Type</th>
                <th>Default</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td>color</td>
                <td>Predefined colors</td>
                <td>
                  main | alternative | positive | warning | danger | neutral
                </td>
                <td>
                  <code>main</code>
                </td>
              </tr>
              <tr>
                <td>type</td>
                <td>Predefined types</td>
                <td>
                  primary | secondary | secondaryAltA | secondaryAltB | link
                </td>
                <td>
                  <code>primary</code>
                </td>
              </tr>
              <tr>
                <td>size</td>
                <td>xsmall is only available for link style type.</td>
                <td>large | medium | small | xsmall</td>
                <td>
                  <code>medium</code>
                </td>
              </tr>
              <tr>
                <td>icononly</td>
                <td>For icon only button</td>
                <td>boolean</td>
                <td>-</td>
              </tr>
              <tr>
                <td>disabled</td>
                <td>Disabled state of button</td>
                <td>boolean</td>
                <td>false</td>
              </tr>
              <tr>
                <td>href</td>
                <td>Redirect url of link button</td>
                <td>string</td>
                <td>-</td>
              </tr>
              <tr>
                <td>target</td>
                <td>
                  Same as target attribute of a, works when href is specified
                </td>
                <td>string</td>
                <td>-</td>
              </tr>
              <tr>
                <td>onClick</td>
                <td>
                  Set the handler to handle <code>click</code> event
                </td>
                <td>(event) =&gt; void</td>
                <td>-</td>
              </tr>
              <tr>
                <td>...others</td>
                <td>
                  All extra props are passed down to the{" "}
                  <>
                    <AxButton
                      size="small"
                      type="link"
                      target="_blank"
                      href="https://4x.ant.design/components/button/#API"
                    >
                      {`<Button />`}
                    </AxButton>
                  </>{" "}
                  component.
                </td>
                <td></td>
                <td>-</td>
              </tr>           
            </tbody>
          </table>
        </Space>

        <AxDivider orientation="left" plain>
          Colors
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`
<AxButton color="main" type="primary">
  Primary
</AxButton>

<AxButton color="alternative" type="primary">
  Alternative
</AxButton>

<AxButton color="positive" type="primary">
  Positive
</AxButton>

<AxButton color="warning" type="primary">
  Warning
</AxButton>

<AxButton color="danger" type="primary">
  Danger
</AxButton>

<AxButton color="neutral" type="primary">
  Neutral
</AxButton>
`}</pre>
          <Space>
            <AxButton color="main" type="primary">
              Primary
            </AxButton>

            <AxButton color="alternative" type="primary">
              Alternative
            </AxButton>

            <AxButton color="positive" type="primary">
              Positive
            </AxButton>

            <AxButton color="warning" type="primary">
              Warning
            </AxButton>

            <AxButton color="danger" type="primary">
              Danger
            </AxButton>

            <AxButton color="neutral" type="primary">
              Neutral
            </AxButton>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Type
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`
<AxButton color="main" type="primary">
  Primary
</AxButton>

<AxButton color="main" type="secondary">
  Secondary
</AxButton>

<AxButton color="main" type="secondaryAltA">
  Secondary Alt A
</AxButton>

<AxButton color="main" type="secondaryAltB">
  Secondary Alt B
</AxButton>

<AxButton color="main" type="link">
  Link
</AxButton>
`}</pre>
          <Space>
            <AxButton color="main" type="primary">
              Primary
            </AxButton>

            <AxButton color="main" type="secondary">
              Secondary
            </AxButton>

            <AxButton color="main" type="secondaryAltA">
              Secondary Alt A
            </AxButton>

            <AxButton color="main" type="secondaryAltB">
              Secondary Alt B
            </AxButton>

            <AxButton color="main" type="link">
              Link
            </AxButton>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Size
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`

<AxButton color="main" type="primary" size="large">
  Large
</AxButton>

<AxButton color="main" type="primary" size="medium">
  Medium
</AxButton>

<AxButton color="main" type="primary" size="small">
  Small
</AxButton>

<AxButton color="main" type="link" size="xsmall">
  Xsmall
</AxButton>
`}</pre>
          <Space>
            <AxButton color="main" type="primary" size="large">
              Large
            </AxButton>

            <AxButton color="main" type="primary" size="medium">
              Medium
            </AxButton>

            <AxButton color="main" type="primary" size="small">
              Small
            </AxButton>

            <AxButton color="main" type="link" size="xsmall">
              Xsmall
            </AxButton>
          </Space>
        </Space>

        <AxDivider orientation="left" plain>
          Icon Only
        </AxDivider>

        <Space size="large" direction="vertical">
          <pre>{`
<AxButton color="main" type="primary" size="large" icononly>
  <i className="fa-regular fa-star"></i>
</AxButton>

<AxButton color="main" type="primary" size="medium" icononly>
  <i className="fa-regular fa-star"></i>
</AxButton>

<AxButton color="main" type="primary" size="small" icononly>
  <i className="fa-regular fa-star"></i>
</AxButton>

<AxButton color="main" type="link" size="xsmall" icononly>
  <i className="fa-regular fa-star"></i>
</AxButton>
`}</pre>
          <Space>
            <AxButton color="main" type="primary" size="large" icononly>
              <i className="fa-regular fa-star"></i>
            </AxButton>

            <AxButton color="main" type="primary" size="medium" icononly>
              <i className="fa-regular fa-star"></i>
            </AxButton>

            <AxButton color="main" type="primary" size="small" icononly>
              <i className="fa-regular fa-star"></i>
            </AxButton>

            <AxButton color="main" type="link" size="xsmall" icononly>
              <i className="fa-regular fa-star"></i>
            </AxButton>
          </Space>
        </Space>
      </div>
    );
  }
}
export default Button;
